import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const CHAINS = [
  { id: 'robinhood', label: 'Robinhood' },
  { id: 'base', label: 'Base' },
  { id: 'ethereum', label: 'Ethereum' },
  { id: 'bsc', label: 'BSC' },
];

function fmtAddr(a) {
  if (!a) return '—';
  const s = String(a);
  return s.length > 14 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
}

function socialLine(s = {}) {
  const parts = [];
  if (s.website) parts.push('Web');
  if (s.twitter) parts.push('𝕏');
  if (s.telegram) parts.push('Tg');
  if (s.discord) parts.push('Dc');
  if (s.farcaster) parts.push('FC');
  return parts.length ? parts.join(' · ') : '—';
}

const emptyForm = {
  name: '',
  chain: 'robinhood',
  adapter: 'pons_v2',
  factoryAddress: '',
  exploreUrl: '',
  tokenUrlTemplate: 'https://www.ponsfamily.com/launchpad/{address}',
  enabled: true,
  requireWebsite: true,
  requireSocial: true,
  maxTaxPercent: 4,
  minBondingPercent: 10,
  maxBondingPercent: 99,
  requireUnmigrated: true,
  minUniqueBuyers: 8,
  minHolders: 8,
  minBuyVolumeEth: 0.2,
  maxSellBuyRatioPercent: 55,
  maxTopHolderPercent: 35,
  lookbackBlocks: 1500,
};

export default function Pings() {
  const [pageTab, setPageTab] = useState('feed'); // feed | launchpads
  const [settings, setSettings] = useState(null);
  const [adapters, setAdapters] = useState([]);
  const [pings, setPings] = useState([]);
  const [counts, setCounts] = useState({ pinged: 0, skipped: 0, failed: 0, watching: 0, all: 0 });
  const [statusFilter, setStatusFilter] = useState('pinged');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const statusQ = statusFilter ? `?status=${encodeURIComponent(statusFilter)}&limit=60` : '?limit=60';
      const [st, list] = await Promise.all([
        api.get('/pings/settings'),
        api.get(`/pings/pings${statusQ}`),
      ]);
      setSettings(st.data.settings || null);
      setAdapters(st.data.adapters || []);
      setPings(list.data.pings || []);
      if (list.data.counts) setCounts(list.data.counts);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const saveGlobal = async (patch) => {
    if (!settings) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await api.put('/pings/settings', {
        settings: { ...settings, ...patch },
      });
      setSettings(res.data.settings);
      setMsg('Saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (lp) => {
    setEditingId(lp.id);
    setForm({
      name: lp.name || '',
      chain: lp.chain || 'robinhood',
      adapter: lp.adapter || 'pons_v2',
      factoryAddress: lp.factoryAddress || '',
      exploreUrl: lp.exploreUrl || '',
      tokenUrlTemplate: lp.tokenUrlTemplate || '',
      enabled: lp.enabled !== false,
      requireWebsite: lp.filters?.requireWebsite !== false,
      requireSocial: lp.filters?.requireSocial !== false,
      maxTaxPercent: lp.filters?.maxTaxPercent ?? 4,
      minBondingPercent: lp.filters?.minBondingPercent ?? 10,
      maxBondingPercent: lp.filters?.maxBondingPercent ?? 99,
      requireUnmigrated: lp.filters?.requireUnmigrated !== false,
      minUniqueBuyers: lp.filters?.minUniqueBuyers ?? 8,
      minHolders: lp.filters?.minHolders ?? 8,
      minBuyVolumeEth: lp.filters?.minBuyVolumeEth ?? 0.2,
      maxSellBuyRatioPercent: lp.filters?.maxSellBuyRatioPercent ?? 55,
      maxTopHolderPercent: lp.filters?.maxTopHolderPercent ?? 35,
      lookbackBlocks: lp.lookbackBlocks || 1500,
    });
    setPageTab('launchpads');
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const saveLaunchpad = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const body = {
        id: editingId || undefined,
        name: form.name,
        chain: form.chain,
        adapter: form.adapter,
        factoryAddress: form.factoryAddress,
        exploreUrl: form.exploreUrl || null,
        tokenUrlTemplate: form.tokenUrlTemplate,
        enabled: form.enabled,
        lookbackBlocks: Number(form.lookbackBlocks) || 1500,
        filters: {
          requireWebsite: !!form.requireWebsite,
          requireSocial: !!form.requireSocial,
          maxTaxPercent: Number(form.maxTaxPercent),
          minBondingPercent: Number(form.minBondingPercent),
          maxBondingPercent: Number(form.maxBondingPercent),
          requireUnmigrated: !!form.requireUnmigrated,
          minUniqueBuyers: Number(form.minUniqueBuyers),
          minHolders: Number(form.minHolders),
          minBuyVolumeEth: Number(form.minBuyVolumeEth),
          maxSellBuyRatioPercent: Number(form.maxSellBuyRatioPercent),
          maxTopHolderPercent: Number(form.maxTopHolderPercent),
        },
      };
      const res = editingId
        ? await api.put(`/pings/launchpads/${editingId}`, body)
        : await api.post('/pings/launchpads', body);
      setSettings(res.data.settings);
      resetForm();
      setMsg(editingId ? 'Launchpad updated' : 'Launchpad added');
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save launchpad');
    } finally {
      setSaving(false);
    }
  };

  const removeLaunchpad = async (lp) => {
    if (!window.confirm(`Remove launchpad ${lp.name}?`)) return;
    try {
      const res = await api.delete(`/pings/launchpads/${lp.id}`);
      setSettings(res.data.settings);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const runScan = async () => {
    setScanning(true);
    setError(null);
    try {
      await api.post('/pings/scan');
      await fetchAll();
      setMsg('Scan complete');
    } catch (err) {
      setError(err.response?.data?.error || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading pings…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            New launchpad launches → filter → admin group. Starts with{' '}
            <a
              href="https://www.ponsfamily.com/launchpad"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
            >
              Pons
            </a>
            .
          </p>
          <div className="mt-3 flex gap-1">
            {[
              { id: 'feed', label: 'Feed' },
              { id: 'launchpads', label: 'Launchpads' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPageTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  pageTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={runScan}
            disabled={scanning}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm disabled:opacity-50"
          >
            {scanning ? 'Scanning…' : 'Scan now'}
          </button>
          {settings && (
            <button
              type="button"
              disabled={saving}
              onClick={() => saveGlobal({ enabled: !settings.enabled })}
              className={`px-3 py-2 rounded-lg text-sm text-white ${
                settings.enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 hover:bg-gray-600'
              }`}
            >
              {settings.enabled ? 'Pings on' : 'Pings off'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
      )}
      {msg && <div className="text-sm text-green-600">{msg}</div>}

      {settings && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500">Pinged</div>
            <div className="text-xl font-semibold text-green-600">{counts.pinged}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Watching</div>
            <div className="text-xl font-semibold text-blue-600">{counts.watching || 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Skipped</div>
            <div className="text-xl font-semibold text-amber-600">{counts.skipped}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Launchpads</div>
            <div className="text-xl font-semibold">{settings.launchpads?.length || 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Poll / notify</div>
            <div className="font-medium">
              {Math.round((settings.pollIntervalMs || 0) / 1000)}s ·{' '}
              {settings.notifyEnabled !== false ? 'Cook Pings on' : 'notify off'}
            </div>
          </div>
        </div>
      )}

      {pageTab === 'feed' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
            {[
              { id: 'pinged', label: 'Pinged', count: counts.pinged },
              { id: 'watching', label: 'Watching', count: counts.watching || 0 },
              { id: 'skipped', label: 'Skipped', count: counts.skipped },
              { id: '', label: 'All', count: counts.all },
            ].map((t) => (
              <button
                key={t.id || 'all'}
                type="button"
                onClick={() => setStatusFilter(t.id)}
                className={`px-3 py-1 rounded text-sm ${
                  statusFilter === t.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                {t.label} <span className="opacity-70">{t.count}</span>
              </button>
            ))}
          </div>
          {!pings.length ? (
            <div className="p-6 text-sm text-gray-500">
              No pings yet. Worker watches enabled launchpads; matching launches (website + social + tax
              under max) go to the admin group.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Token</th>
                    <th className="px-4 py-2">Launchpad</th>
                    <th className="px-4 py-2">Tax</th>
                    <th className="px-4 py-2">Bonding</th>
                    <th className="px-4 py-2">Links</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {pings.map((p) => (
                    <tr key={p._id} className="text-gray-900 dark:text-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.symbol || '—'}</div>
                        <div className="text-xs text-gray-500">{p.name}</div>
                        <div className="text-xs font-mono text-gray-500">{fmtAddr(p.tokenAddress)}</div>
                        {p.tokenUrl && (
                          <a
                            href={p.tokenUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Open
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div>{p.launchpadName || p.launchpadId}</div>
                        <div className="uppercase text-gray-500">{p.chain}</div>
                      </td>
                      <td className="px-4 py-3">
                        {p.taxPercent != null ? `${p.taxPercent}%` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {p.bondingPercent != null ? `${Number(p.bondingPercent).toFixed(1)}%` : '—'}
                        {(p.uniqueBuyers != null || p.holders != null) && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {p.uniqueBuyers != null ? `${p.uniqueBuyers} buy` : ''}
                            {p.uniqueBuyers != null && p.holders != null ? ' · ' : ''}
                            {p.holders != null ? `${p.holders} hold` : ''}
                            {p.buyVolumeEth != null
                              ? ` · ${Number(p.buyVolumeEth).toFixed(2)} ETH`
                              : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">{socialLine(p.socials)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            p.status === 'pinged'
                              ? 'bg-green-100 text-green-800'
                              : p.status === 'watching'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {p.status}
                        </span>
                        {p.skipReason && (
                          <div className="text-xs text-gray-500 mt-1 max-w-[200px]" title={p.skipReason}>
                            {p.skipReason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {p.launchedAt || p.createdAt
                          ? new Date(p.launchedAt || p.createdAt).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pageTab === 'launchpads' && settings && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4 max-w-3xl">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {editingId ? 'Edit launchpad' : 'Add launchpad'}
              </h2>
              {editingId && (
                <button type="button" onClick={resetForm} className="text-sm text-gray-500">
                  Cancel edit
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500">
              Multichain-ready. Adapter <code className="text-xs">pons_v2</code> watches a Pons-style
              factory for <code className="text-xs">TokenLaunched</code>. Default filters: website +
              social + tax &lt; 4% + bonding ≥ 10% (under-bonded launches stay in Watching until they
              hit the threshold).
            </p>
            <form onSubmit={saveLaunchpad} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Pons"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Chain</label>
                  <select
                    value={form.chain}
                    onChange={(e) => setForm((f) => ({ ...f, chain: e.target.value }))}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  >
                    {CHAINS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Adapter</label>
                  <select
                    value={form.adapter}
                    onChange={(e) => setForm((f) => ({ ...f, adapter: e.target.value }))}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  >
                    {(adapters.length ? adapters : [{ id: 'pons_v2', label: 'Pons V2' }]).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label || a.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Factory address</label>
                  <input
                    required
                    value={form.factoryAddress}
                    onChange={(e) => setForm((f) => ({ ...f, factoryAddress: e.target.value }))}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono"
                    placeholder="0x…"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Explore URL</label>
                  <input
                    value={form.exploreUrl}
                    onChange={(e) => setForm((f) => ({ ...f, exploreUrl: e.target.value }))}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="https://www.ponsfamily.com/launchpad"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Token URL template</label>
                  <input
                    value={form.tokenUrlTemplate}
                    onChange={(e) => setForm((f) => ({ ...f, tokenUrlTemplate: e.target.value }))}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="https://…/{address}"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                <div className="text-sm font-medium">Filters</div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requireWebsite}
                    onChange={(e) => setForm((f) => ({ ...f, requireWebsite: e.target.checked }))}
                  />
                  Require website
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requireSocial}
                    onChange={(e) => setForm((f) => ({ ...f, requireSocial: e.target.checked }))}
                  />
                  Require social (𝕏 / Tg / Discord / Farcaster)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requireUnmigrated}
                    onChange={(e) => setForm((f) => ({ ...f, requireUnmigrated: e.target.checked }))}
                  />
                  Only unmigrated (still on bonding curve — skip graduated/DEX)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max tax %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.maxTaxPercent}
                      onChange={(e) => setForm((f) => ({ ...f, maxTaxPercent: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min bonding %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={form.minBondingPercent}
                      onChange={(e) => setForm((f) => ({ ...f, minBondingPercent: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max bonding % (still on curve)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={form.maxBondingPercent}
                      onChange={(e) => setForm((f) => ({ ...f, maxBondingPercent: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min unique buyers</label>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={form.minUniqueBuyers}
                      onChange={(e) => setForm((f) => ({ ...f, minUniqueBuyers: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min holders</label>
                    <input
                      type="number"
                      min="0"
                      max="5000"
                      value={form.minHolders}
                      onChange={(e) => setForm((f) => ({ ...f, minHolders: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min buy volume (ETH)</label>
                    <input
                      type="number"
                      min="0"
                      max="10000"
                      step="0.05"
                      value={form.minBuyVolumeEth}
                      onChange={(e) => setForm((f) => ({ ...f, minBuyVolumeEth: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max sell/buy % (anti dump)</label>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      step="1"
                      value={form.maxSellBuyRatioPercent}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, maxSellBuyRatioPercent: e.target.value }))
                      }
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max top holder %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={form.maxTopHolderPercent}
                      onChange={(e) => setForm((f) => ({ ...f, maxTopHolderPercent: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Lookback blocks (first run)</label>
                    <input
                      type="number"
                      min="100"
                      max="20000"
                      value={form.lookbackBlocks}
                      onChange={(e) => setForm((f) => ({ ...f, lookbackBlocks: e.target.value }))}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Set a quality field to 0 to disable it. Under thresholds stay in Watching until they
                  pass or expire.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                  Enabled
                </label>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingId ? 'Update launchpad' : 'Add launchpad'}
              </button>
            </form>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 font-semibold">
              Configured launchpads
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Chain / adapter</th>
                    <th className="px-4 py-2">Filters</th>
                    <th className="px-4 py-2">Cursor</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(settings.launchpads || []).map((lp) => (
                    <tr key={lp.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{lp.name}</div>
                        <div className="text-xs font-mono text-gray-500">{fmtAddr(lp.factoryAddress)}</div>
                        {lp.exploreUrl && (
                          <a
                            href={lp.exploreUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Explore
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs uppercase">
                        {lp.chain} · {lp.adapter}
                        <div className="normal-case text-gray-500">
                          {lp.enabled ? 'enabled' : 'disabled'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        web {lp.filters?.requireWebsite ? '✓' : '—'} · social{' '}
                        {lp.filters?.requireSocial ? '✓' : '—'} · tax &lt;{' '}
                        {lp.filters?.maxTaxPercent ?? 4}% · bond ≥{' '}
                        {lp.filters?.minBondingPercent ?? 10}%
                        –{lp.filters?.maxBondingPercent ?? 99}%
                        {lp.filters?.requireUnmigrated !== false ? ' · curve only' : ''}
                        <div className="text-gray-500 mt-0.5">
                          buyers ≥ {lp.filters?.minUniqueBuyers ?? 8} · holders ≥{' '}
                          {lp.filters?.minHolders ?? 8} · vol ≥{' '}
                          {lp.filters?.minBuyVolumeEth ?? 0.2} · sell/buy ≤{' '}
                          {lp.filters?.maxSellBuyRatioPercent ?? 55}% · top ≤{' '}
                          {lp.filters?.maxTopHolderPercent ?? 35}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono">
                        {lp.lastBlock != null ? lp.lastBlock : 'not started'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(lp)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeLaunchpad(lp)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 max-w-xl space-y-3">
            <div className="font-semibold text-sm">Global</div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings.notifyEnabled !== false}
                onChange={(e) => saveGlobal({ notifyEnabled: e.target.checked })}
              />
              Notify Cook Pings Telegram group
            </label>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Telegram chat ID (Cook Pings)</label>
              <input
                type="text"
                defaultValue={settings.telegramChatId || ''}
                onBlur={(e) => {
                  const v = String(e.target.value || '').trim();
                  if (v && v !== String(settings.telegramChatId || '')) {
                    saveGlobal({ telegramChatId: v });
                  }
                }}
                className="w-full max-w-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono"
                placeholder="-5383902849"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Poll interval (ms)</label>
              <input
                type="number"
                min="10000"
                step="1000"
                defaultValue={settings.pollIntervalMs}
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v && v !== settings.pollIntervalMs) saveGlobal({ pollIntervalMs: v });
                }}
                className="w-40 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Restart cookbot-worker to apply a new interval.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
